import type { Condition, Reference } from '@medplum/fhirtypes'
import type { BasePlan } from '@seen/core/models/care-plans/BasePlan'
import { CarePlanProblem } from '@seen/core/models/conditions/CarePlanProblem'
import { ExtendedCondition } from '@seen/core/models/conditions/ExtendedCondition'

import { Badge, Button, cn } from '@seen/ui'
import { IconX } from '@tabler/icons-react'
import { type JSX, useState } from 'react'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Skeleton } from '#src/components/core/Skeleton.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function CarePlanCondition({
  condition: conditionRef,
  carePlan,
  onDelete,
}: {
  condition: Reference<Condition>
  carePlan: BasePlan
  onDelete?: (carePlan: BasePlan, condition: ExtendedCondition) => void
}): JSX.Element | null {
  const { data: condition, isLoading } = useRead(
    ExtendedCondition,
    conditionRef
  )
  const isReadOnly = useIsReadOnly()
  const [isEditing, setIsEditing] = useState(false)

  useErrorToast(
    !isLoading && !condition
      ? `Missing problem: ${conditionRef.reference}`
      : undefined
  )

  if (isLoading || !condition) {
    return <Skeleton className="h-16" />
  }

  return (
    <div className="group flex items-center gap-6">
      <div className="flex grow flex-col">
        <div className="flex items-center">
          <ResourceFieldForm
            instance={condition}
            valueFn={(condition) => condition.title}
            patchFn={(patchCondition, sanitizedHtml) => {
              patchCondition.title = sanitizedHtml
            }}
            debounceTyping
            render={({ field }) => (
              <RichTextEditor
                placeholder="Problem Title"
                variant="unstyled"
                onFocus={() => {
                  setIsEditing(true)
                }}
                editable={!condition.elationId}
                {...field}
              />
            )}
          />
          <span className="ml-2 flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {condition.categoryDisplay}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {condition.clinicalStatus}
            </Badge>
          </span>
          <DevMedplumAppLinkOverlay to={condition} inline />
        </div>

        {CarePlanProblem.isInstance(condition) && (
          <ResourceFieldForm
            className={cn(isEditing || condition.note ? 'block' : 'hidden')}
            instance={condition}
            valueFn={(condition) => condition.note}
            patchFn={(patchCondition, sanitizedHtml) => {
              patchCondition.note = sanitizedHtml
            }}
            debounceTyping
            render={({ field }) => (
              <RichTextEditor
                editable={carePlan.isEditable}
                placeholder="Add a note…"
                variant="unstyled"
                className="text-foreground-secondary"
                {...field}
              />
            )}
          />
        )}
      </div>
      {!isReadOnly && carePlan.isEditable && (
        <div className="invisible group-hover:visible">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete?.(carePlan, condition)}
          >
            <IconX className="size-4" />
          </Button>
        </div>
      )}
    </div>
  )
}
